/**
 * Help overlays: About and the shortcut list. Opened from the Help menu.
 */

import * as React from "react";
import { DialogOverlay, DialogPanel, dialogSecondaryButtonClass } from "@/components/ui/dialog";
import { Button } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/shell/BrandMark";
import { useSystemStatus } from "@/store/appStore";

type Overlay = "about" | "shortcuts" | null;

let show: ((kind: Overlay) => void) | null = null;

export function openHelpOverlay(kind: Exclude<Overlay, null>): void {
  show?.(kind);
}

export function HelpOverlays() {
  const [kind, setKind] = React.useState<Overlay>(null);
  const status = useSystemStatus();

  React.useEffect(() => {
    show = setKind;
    return () => {
      if (show === setKind) show = null;
    };
  }, []);

  if (!kind) return null;

  return (
    <DialogOverlay className="z-[70]" onMouseDown={() => setKind(null)}>
      <DialogPanel className="w-[480px] px-6 py-6" onMouseDown={(event) => event.stopPropagation()}>
        {kind === "about" ? (
          <>
            <BrandMark size="lg" />
            <p className="mt-3 text-[12.5px] text-ads-muted">
              Version {status?.appVersion ?? "—"}. Acute DWI / PWI analysis on a local workstation.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-[17px] font-semibold tracking-tight text-ads-text">Keyboard shortcuts</h2>
            <dl className="mt-3 space-y-1.5 text-[12.5px]">
              <Row keys="Ctrl+Shift+P" label="Command Palette" />
              <Row keys="Ctrl+F1" label="Pin / collapse ribbon" />
              <Row keys="Esc" label="Close menus and palettes" />
            </dl>
          </>
        )}
        <Button size="sm" className={cn("mt-5", dialogSecondaryButtonClass)} onClick={() => setKind(null)}>
          Close
        </Button>
      </DialogPanel>
    </DialogOverlay>
  );
}

function Row({ keys, label }: { keys: string; label: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-ads-text">{label}</dt>
      <dd className="font-mono text-[11px] text-ads-muted">{keys}</dd>
    </div>
  );
}
